import { tagularData, resetTagularData } from '../tagular'
import { TagularTrackingService } from '../tracking/TagularTrackingService'
import {
  EventName,
  FormName,
  SimpleV2BaseEvent,
  SimpleV2OfframpEvent,
  SimpleV2PlaidExitedEvent,
  SimpleV2PlaidExitOutcome,
  SimpleV2ReasonCode,
  SimpleV2ReasonCategory,
  SIMPLE_V2_REASON_CODE,
  SIMPLE_V2_REASON_CATEGORY,
} from '@mortgage-pos/types'

const OFFRAMP_CATEGORY: Record<SimpleV2ReasonCode, SimpleV2ReasonCategory> = {
  [SIMPLE_V2_REASON_CODE.multiBorrower]: SIMPLE_V2_REASON_CATEGORY.eligibility,
  [SIMPLE_V2_REASON_CODE.noAddress]: SIMPLE_V2_REASON_CATEGORY.data,
  [SIMPLE_V2_REASON_CODE.nonW2Primary]: SIMPLE_V2_REASON_CATEGORY.eligibility,
  [SIMPLE_V2_REASON_CODE.plaidOptOut]: SIMPLE_V2_REASON_CATEGORY.userBehavior,
  [SIMPLE_V2_REASON_CODE.plaidFail]: SIMPLE_V2_REASON_CATEGORY.data,
  [SIMPLE_V2_REASON_CODE.plaidAbandon]: SIMPLE_V2_REASON_CATEGORY.userBehavior,
  [SIMPLE_V2_REASON_CODE.incomeConfidenceLow]: SIMPLE_V2_REASON_CATEGORY.data,
  [SIMPLE_V2_REASON_CODE.dtiUnviable]: SIMPLE_V2_REASON_CATEGORY.eligibility,
  [SIMPLE_V2_REASON_CODE.noViableProducts]:
    SIMPLE_V2_REASON_CATEGORY.eligibility,
  [SIMPLE_V2_REASON_CODE.creditFrozen]: SIMPLE_V2_REASON_CATEGORY.data,
  [SIMPLE_V2_REASON_CODE.simplev2Abandonment]:
    SIMPLE_V2_REASON_CATEGORY.userBehavior,
}

class SimpleV2TrackingService extends TagularTrackingService {
  private formId = ''
  private formName = FormName.SimpleV2Application
  private hasTrackedOfframp = false

  init(
    id: string,
    pageName: string,
    stepNumber: number,
    formName = FormName.SimpleV2Application
  ) {
    if (!id || this.formId === id) return
    this.formId = id
    this.formName = formName
    this.hasTrackedOfframp = false
    this.simpleV2Attempted(pageName, stepNumber)
  }

  simpleV2Attempted(pageName: string, stepNumber: number) {
    this.track(EventName.SimpleV2Attempted, pageName, stepNumber, {})
  }

  simpleV2PlaidLinkTokenCreated(pageName: string, stepNumber: number) {
    this.track(
      EventName.SimpleV2PlaidLinkTokenCreated,
      pageName,
      stepNumber,
      {}
    )
  }

  simpleV2PlaidConnected(pageName: string, stepNumber: number) {
    this.track(EventName.SimpleV2PlaidConnected, pageName, stepNumber, {})
  }

  simpleV2PlaidError(pageName: string, stepNumber: number) {
    this.track(EventName.SimpleV2PlaidError, pageName, stepNumber, {})
  }

  simpleV2PlaidExited(
    outcome: SimpleV2PlaidExitOutcome,
    pageName: string,
    stepNumber: number
  ) {
    this.track<SimpleV2PlaidExitedEvent>(
      EventName.SimpleV2PlaidExited,
      pageName,
      stepNumber,
      { outcome }
    )
  }

  simpleV2Offramp(
    reasonCode: SimpleV2ReasonCode,
    page: string,
    stepNumber: number
  ) {
    if (this.hasTrackedOfframp) return
    this.hasTrackedOfframp = true

    this.track<SimpleV2OfframpEvent>(
      EventName.SimpleV2Offramp,
      page,
      stepNumber,
      { reasonCode, reasonCategory: OFFRAMP_CATEGORY[reasonCode] }
    )
  }

  simpleV2OfferSelected(pageName: string, stepNumber: number) {
    this.track(EventName.SimpleV2OfferSelected, pageName, stepNumber, {})
  }

  reset() {
    this.formId = ''
    this.formName = FormName.SimpleV2Application
    this.hasTrackedOfframp = false
  }

  private track<E extends SimpleV2BaseEvent = SimpleV2BaseEvent>(
    eventName: EventName,
    pageName: string,
    stepNumber: number,
    fields: Omit<E, keyof SimpleV2BaseEvent>
  ) {
    this.emit<E>(
      eventName,
      {
        formId: this.formId,
        formName: this.formName,
        pageName,
        stepNumber,
      },
      fields
    )
  }
}

export const simpleV2TrackingService = new SimpleV2TrackingService()

export function simpleV2TrackingData(key?: EventName) {
  return tagularData(key)
}

export function resetSimpleV2TrackingData() {
  resetTagularData()
  simpleV2TrackingService.reset()
}
